import styled from "styled-components";
import {radii} from "theme/tokens";

/**
 * Styles for {@link ./BrandColorPalette}, the brand-accent swatch. Externalized per the styling
 * mandate, so the component file stays markup and behaviour.
 *
 * ui#1923 replaced the animated radial disc with this (owner, 2026-09-03: "palette disc can be a
 * simple swatch"). Three things the disc needed are deliberately absent: there is no motion, so no
 * `prefers-reduced-motion` branch is needed to switch any off; there is no geometry module feeding
 * lengths in, because a wrapped row has no geometry to compute; and there is no stacking order to
 * manage, because circles laid out in a row do not overlap.
 *
 * The one rule here that is behaviour rather than taste, inherited from `BrandSettings.styles.ts`: a
 * sender's accent is only ever painted as a BACKGROUND. Nothing here puts text on an accent, so the
 * api-derived foreground has no job in this file except the selected ring, which sits ON a swatch and
 * therefore takes the foreground that swatch's own colour was measured against.
 */

/**
 * The touch target around each circle, and the circle drawn inside it.
 *
 * The target is the accessible size (44px, the repo's mobile floor) while the CIRCLE is the visible
 * one, so the gap a reader sees between two swatches is the two targets' own padding — 8px — rather
 * than a `gap` that would push the row past 44px per column. The disc this replaced drew 34px dots
 * scaled DOWN by its container, so on a narrow sheet its targets were smaller than either number.
 */
const SWATCH_TARGET_PX = 44;
const SWATCH_CIRCLE_PX = 36;

/**
 * The wrapped row. Wrapping is the whole layout: 36 swatches wrap to as many rows as the sheet is
 * wide, five wrap to one, and neither case needs a media query or a column count.
 */
export const SwatchRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	margin: 6px 0;

	&[aria-disabled="true"] {
		opacity: 0.55;
		cursor: not-allowed;
	}
`;

/**
 * One selectable colour: a `radio` in the row's `radiogroup`, so the whole palette is a single tab
 * stop and the arrow keys walk it ({@link ../brandPalette.paletteNavigate}).
 *
 * The button is the transparent target; {@link SwatchCircle} inside it is what is painted. Splitting
 * the two is what lets the target stay 44px while the swatches still read as a tight row.
 */
export const Swatch = styled.button`
	width: ${SWATCH_TARGET_PX}px;
	height: ${SWATCH_TARGET_PX}px;
	display: grid;
	place-items: center;
	padding: 0;
	border: none;
	background: none;
	border-radius: ${radii.pill};
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.ink};
		/* Inset, so the ring stays inside the target and cannot overlap the next swatch's circle. */
		outline-offset: -3px;
	}

	&:disabled {
		cursor: not-allowed;
	}
`;

/**
 * The painted circle. `--swatch` carries the palette hex; hovering rings it in the muted ink so the
 * swatch under the cursor is identifiable before it is clicked, without any motion.
 */
export const SwatchCircle = styled.span`
	position: relative;
	width: ${SWATCH_CIRCLE_PX}px;
	height: ${SWATCH_CIRCLE_PX}px;
	border-radius: ${radii.pill};
	background: var(--swatch);

	${Swatch}:hover:not(:disabled) & {
		box-shadow: 0 0 0 2px ${({theme}) => theme.tokens.ink3};
	}
`;

/**
 * The chosen swatch's marker, drawn INSIDE its circle so selection never encroaches on a neighbour.
 *
 * Its colour is `--ring-ink`, the foreground the API itself would derive for this swatch
 * (`readableForegroundFor`), so it clears WCAG AA against the colour it sits on — a single `theme.ink`
 * would vanish on a navy swatch in light mode, which is exactly the state a brand accent lands in.
 */
export const SwatchSelectedRing = styled.span`
	position: absolute;
	inset: 4px;
	border-radius: ${radii.pill};
	border: 2.5px solid var(--ring-ink);
	pointer-events: none;
`;

/**
 * The "no colour" stop (ui#2416) — the same circle as a colour swatch, but EMPTY and struck through.
 *
 * Owner 2026-09-12, replacing the builder's "Clear colour" pill: "like a swatch null/empty color". It
 * is drawn as an outline plus a slash on a TRANSPARENT ground rather than as a filled circle, because
 * any fill would read as a colour the sender could pick — a white disc in light mode is exactly the
 * mistake this avoids. Transparency also makes it correct in both modes for free: the sheet's own
 * surface shows through, and only the outline and the slash carry ink.
 *
 * `ink2` rather than the `ink3` the hover halo uses: the outline and the slash are the whole glyph, so
 * they carry the meaning, and `ink3` is a muted grey that loses too much of it at 36px in either mode.
 */
export const ClearSwatchCircle = styled.span`
	position: relative;
	width: ${SWATCH_CIRCLE_PX}px;
	height: ${SWATCH_CIRCLE_PX}px;
	border-radius: ${radii.pill};
	/* Clips the slash to the disc, so the glyph is a struck-through circle and not a crossed-out box. */
	overflow: hidden;
	border: 1.5px solid ${({theme}) => theme.tokens.ink2};

	&::after {
		content: "";
		position: absolute;
		top: -20%;
		left: 50%;
		width: 1.5px;
		height: 140%;
		background: ${({theme}) => theme.tokens.ink2};
		/* Overshoots the box and is rotated about its own centre, so the slash spans the full diameter. */
		transform: translateX(-50%) rotate(45deg);
	}

	${Swatch}:hover:not(:disabled) & {
		box-shadow: 0 0 0 2px ${({theme}) => theme.tokens.ink3};
	}
`;

/**
 * The clear stop's chosen marker. Same ring as a colour's, in the page ink: there is no swatch colour
 * here to derive a readable foreground from, and the ring sits on the sheet's own surface.
 */
export const ClearSwatchSelectedRing = styled(SwatchSelectedRing)`
	border-color: ${({theme}) => theme.tokens.ink};
`;
